<template>
  <div>
    <div class="text-h4 mt-2">Diabetic Retinopathy ICD10 Coding Widget</div>
    <div class="caption">
      There are more than 100 possible diabetic retinopathy codes. Use this
      utility to find the right one by clicking one item in each section below.
      Or, you can
      <router-link to="/icd10/diabetes"
        >see the individual codes here</router-link
      >
      if you prefer browsing through long lists.
    </div>
    <div class="d-flex flex-row flex-wrap my-8 align-start">
      <div>
        <div class="d-flex flex-row align-end mr-4 codeWidth">
          <div v-for="(char, index) in codeArr" :key="index" class="">
            <v-fab-transition>
              <span class="d-flex flex-row align-end" v-if="char">
                <span v-if="index == 3 && char" class="bigDecimal">.</span>
                <span class="box" :style="'background-color:' + colors[index]">
                  {{ char }}
                </span>
              </span>
            </v-fab-transition>
          </div>
        </div>
        <div class="caption pr-2 ml-4">{{ codeString }}</div>
      </div>
      <div class="d-flex flex-row mt-2">
        <div class="font-weight-light description">
          <v-fade-transition
            ><div
              v-if="descriptionHTML.type"
              :style="'background-color:' + colors[1]"
              class="px-1 mb-1"
            >
              {{ descriptionHTML.type }}
            </div></v-fade-transition
          >
          <v-fade-transition
            ><div
              v-if="descriptionHTML.path"
              class="px-1 mb-1"
              :style="'background-color:' + colors[3]"
            >
              {{ descriptionHTML.path }}
            </div></v-fade-transition
          >
          <v-fade-transition
            ><div
              v-if="descriptionHTML.edema"
              class="px-1 mb-1"
              :style="'background-color:' + colors[5]"
            >
              {{ descriptionHTML.edema }}
            </div></v-fade-transition
          >
          <v-fade-transition
            ><div
              v-if="descriptionHTML.eye"
              class="px-1 mb-1"
              :style="'background-color:' + colors[6]"
            >
              {{ descriptionHTML.eye }}
            </div></v-fade-transition
          >
        </div>
      </div>
    </div>

    <!-- diabetes type -->
    <div class="">
      <div
        class="sectionHeader font-weight-bold"
        :style="'background-color:' + colors[1]"
      >
        Diabetes Type
      </div>
      <v-radio-group v-model="selected_type" row :hide-details="true">
        <v-radio value="10" label="Type 1">
          <!-- <template v-slot:label>
            Type 1 <span class="subtle">(10)</span>
          </template> -->
        </v-radio>
        <v-radio value="11" label="Type 2">
          <!-- <template v-slot:label>
            Type 2 <span class="subtle">(11)</span>
          </template> -->
        </v-radio>
        <v-radio value="08" label="Due to underlying condition">
          <!-- <template v-slot:label>
            Due to underlying condition <span class="subtle">(08)</span>
          </template> -->
        </v-radio>
        <v-radio value="09" label="Drug or chemical induced"> </v-radio>
        <v-radio value="13" label="Other"> </v-radio>
      </v-radio-group>
    </div>
    <!-- /diabetes type -->

    <!-- eye -->

    <div
      class="sectionHeader d-flex flex-row align-center"
      :style="'background-color:' + colors[6]"
    >
      <div class="font-weight-bold">Eye</div>
      <div class="subtle ml-6">
        <sup>*</sup> Only the pathologies with an asterisk (*) will incorporate
        eye laterality into their ICD10 code.
      </div>
    </div>

    <v-radio-group v-model="selected_eye" row :hide-details="true">
      <v-radio value="1" label="Right Eye"> </v-radio>
      <v-radio value="2" label="Left Eye"> </v-radio>
      <v-radio value="3" label="Both Eyes"> </v-radio>
      <v-radio value="9" label="Unspecified Eye"> </v-radio>
    </v-radio-group>

    <!-- /eye -->

    <!-- pathology -->
    <div
      class="sectionHeader font-weight-bold"
      :style="'background-color:' + colors[3]"
    >
      Pathology
    </div>
    <v-radio-group v-model="selected_pathology" :hide-details="true">
      <v-radio label="No complications" value="9" dense></v-radio>
    </v-radio-group>
    <table class="mt-4 ml-6">
      <tr v-if="!$vuetify.breakpoint.smAndUp">
        <td></td>
        <th>Macular Edema?</th>
      </tr>
      <tr>
        <td
          class="copyVuetify pointer pt-2"
          @click="selected_pathology = '329'"
        >
          Mild {{ nonproliferativeLabel }}<sup>*</sup>
        </td>
        <td :style="edemaBackground">
          <v-radio-group
            v-model="selected_pathology"
            :hide-details="true"
            row
            class="my-0"
          >
            <v-radio :label="macularEdemaLabel.yes" value="321"></v-radio>
            <v-radio :label="macularEdemaLabel.no" value="329"></v-radio>
          </v-radio-group>
        </td>
      </tr>
      <tr>
        <td
          class="copyVuetify pointer pt-2"
          @click="selected_pathology = '339'"
        >
          Moderate {{ nonproliferativeLabel }}<sup>*</sup>
        </td>
        <td :style="edemaBackground">
          <v-radio-group v-model="selected_pathology" :hide-details="true" row>
            <v-radio :label="macularEdemaLabel.yes" value="331"></v-radio>
            <v-radio :label="macularEdemaLabel.no" value="339"></v-radio>
          </v-radio-group>
        </td>
      </tr>
      <tr>
        <td
          class="copyVuetify pointer pt-2"
          @click="selected_pathology = '349'"
        >
          Severe {{ nonproliferativeLabel }}<sup>*</sup>
        </td>
        <td :style="edemaBackground">
          <v-radio-group v-model="selected_pathology" :hide-details="true" row>
            <v-radio :label="macularEdemaLabel.yes" value="341"></v-radio>
            <v-radio :label="macularEdemaLabel.no" value="349"></v-radio>
          </v-radio-group>
        </td>
      </tr>

      <tr>
        <td
          class="copyVuetify pointer pt-2"
          @click="selected_pathology = '359'"
        >
          {{ proliferativeLabel }}<sup>*</sup>
        </td>
        <td :style="edemaBackground">
          <v-radio-group v-model="selected_pathology" :hide-details="true" row>
            <v-radio :label="macularEdemaLabel.yes" value="351"></v-radio>
            <v-radio :label="macularEdemaLabel.no" value="359"></v-radio>
          </v-radio-group>
        </td>
      </tr>
      <tr>
        <td
          class="copyVuetify pointer pt-2"
          @click="selected_pathology = '319'"
        >
          Unspecified retinopathy
        </td>
        <td :style="edemaBackground">
          <v-radio-group v-model="selected_pathology" :hide-details="true" row>
            <v-radio :label="macularEdemaLabel.yes" value="311"></v-radio>
            <v-radio :label="macularEdemaLabel.no" value="319"></v-radio>
          </v-radio-group>
        </td>
      </tr>
    </table>
    <div class="overline mt-4">Less common options</div>
    <v-radio-group v-model="selected_pathology" row>
      <!-- <v-radio label="No complications" value="9"></v-radio> -->
      <v-radio value="352">
        <template v-slot:label>
          Proliferative with tractional RD involving macula<sup>*</sup>
        </template>
      </v-radio>
      <v-radio value="353">
        <template v-slot:label>
          <span
            >Proliferative with tractional RD <i>not</i> involving macula<sup
              >*</sup
            ></span
          >
        </template></v-radio
      >
      <v-radio value="355">
        <template v-slot:label>
          Stable proliferative retinopathy<sup>*</sup>
        </template>
      </v-radio>
      <v-radio label="With diabetic cataract" value="36"></v-radio>
      <v-radio value="37">
        <template v-slot:label>
          With diabetic macular edema, resolved following treatment<sup>*</sup>
        </template></v-radio
      >
      <v-radio
        label="With other diabetic ophthalmic complication"
        value="39"
      ></v-radio>
    </v-radio-group>

    <div class="'d-flex flex-row flex-wrap mt-4"></div>
  </div>
</template>

<script>
// import diabetesRadios from "@/assets/js/icd/diabetesRadios";

export default {
  name: "DiabetesWidget",
  props: {},
  data: function () {
    return {
      //codeArr: ["E", "1", "1", "9", null, null, null],
      colors: [
        "#f6f6f6",
        "#ffd0d0",
        "#ffd0d0",
        "#ffe7bb",
        "#ffe7bb",
        "#e7ffdc",
        "#d8e9ff",
      ],

      selected_pathology: "9",
      selected_type: "11",
      selected_eye: "3",
      typeDescriptions: {
        8: "Diabetes mellitus due to underlying condition",
        9: "Drug or chemical induced diabetes mellitus",
        11: "Type 2 diabetes mellitus",
        10: "Type 1 diabetes mellitus",
        13: "Other specified diabetes mellitus",
      },
      char45Descriptions: {
        9: "without complications",
        31: "with unspecified diabetic retinopathy",
        32: "with mild nonproliferative diabetic retinopathy",
        33: "with moderate nonproliferative diabetic retinopathy",
        34: "with severe nonproliferative diabetic retinopathy",
        35: "with proliferative diabetic retinopathy",
        36: "with diabetic cataract",
        37: "with diabetic macular edema, resolved following treatment",
        39: "with other diabetic ophthalmic complication",
      },
      edemaDescriptions: {
        1: "with macular edema",
        9: "without macular edema",
      },
      eyeDescriptions: {
        1: "right eye",
        2: "left eye",
        3: "both eyes",
        9: "unsepecified eye",
      },
    };
  },
  methods: {
    setChars(charArr, valArr) {
      for (let i = 0; i < charArr.length; i++) {
        let char = charArr[i];
        let val = valArr[i];
        this.codeArr[char - 1] = val; // char-1 to get the codeArr index from the character
      }
    },
  },
  computed: {
    edemaBackground: function () {
      return "background-color: " + this.colors[5];
    },
    codeArr: function () {
      let rtn = ["E"];
      rtn.push(this.selected_type.charAt(0));
      rtn.push(this.selected_type.charAt(1));
      rtn.push(this.selected_pathology.charAt(0));
      if (this.selected_pathology.length > 1) {
        rtn.push(this.selected_pathology.charAt(1));
      }
      if (this.selected_pathology.length > 2) {
        rtn.push(this.selected_pathology.charAt(2));
      }
      if (["2", "3", "4", "5", "7"].indexOf(rtn[4]) > -1) {
        rtn[6] = this.selected_eye;
      }
      return rtn;
    },
    codeString: function () {
      let code = [...this.codeArr];
      if (code.length > 3) code.splice(3, 0, ".");
      return code.join("");
    },
    macularEdemaLabel: function () {
      let rtn = { no: "No macular edema", yes: "Macular edema" };
      if (!this.$vuetify.breakpoint.smAndUp) rtn = { no: "No", yes: "Yes" };
      return rtn;
    },
    nonproliferativeLabel: function () {
      return this.$vuetify.breakpoint.smAndUp
        ? "nonproliferative retinopathy"
        : "nonproliferative";
    },
    proliferativeLabel: function () {
      return this.$vuetify.breakpoint.smAndUp
        ? "Proliferative retinopathy"
        : "Proliferative.";
    },
    descriptionHTML: function () {
      let rtn = { type: null, path: null, edema: null, eye: null };

      //   rtn += `<b>${this.codeString}</b> `;
      rtn.type = this.typeDescriptions[parseInt(this.selected_type)];
      let pathKey = this.selected_pathology;
      let edemaKey = null;
      let char45Key = pathKey;
      if (pathKey.length > 2) {
        char45Key = pathKey.substring(0, 2);
        edemaKey = pathKey[pathKey.length - 1];
      }
      rtn.path = this.char45Descriptions[parseInt(char45Key)];
      if (edemaKey) rtn.edema = this.edemaDescriptions[parseInt(edemaKey)];
      if (this.codeArr.length == 7)
        rtn.eye = this.eyeDescriptions[parseInt(this.codeArr[6])];
      return rtn;
    },
  },
};
</script>

<style scoped>
.box {
  padding: 1px 5px;
  margin: 1px;
  background-color: lightgray;
  font-size: 18pt;
  border: 1px solid gray;
}
.bigDecimal {
  font-weight: bold;
  font-size: 24pt;
}
/* .optionsSection {
  display: inline-block;
  margin-right: 2em;
} */
.v-input--selection-controls {
  margin-top: 0 !important;
}
.sectionHeader {
  /* background-color: #e9f2f5; */
  padding: 6px 8px;
  margin-top: 1em;
}

.v-input--radio-group {
  margin-left: 1em;
  padding-top: 10px;
}
.subtle {
  margin-left: 6px;
  font-size: smaller;
  color: #6a6a6a;
}
/* table{ margin: 1em 0;} */
table td,
th {
  /* text-align: center; */
  /* vertical-align: top; */
  padding-right: 1.5em;
  border: none;
}
label,
.copyVuetify {
  font-size: 16px;
  line-height: 1;
  min-height: 8px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  color: rgba(0, 0, 0, 0.6);
  font-family: "Roboto", sans-serif;
  margin-right: 1em;
  margin-left: 0.5em;
}
.pointer {
  cursor: pointer;
}
.codeWidth {
  min-width: 225px;
}
.description {
  min-height: 80px;
  min-width: 80px;
}
</style>
